import { useState } from "react";
import { useLocation } from "wouter";
import {
  useUpdateProfile,
  useGetProfile,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const { data: profile } = useGetProfile();
  const updateProfile = useUpdateProfile();

  const [profileData, setProfileData] = useState({
    name: "",
    country: "Australia",
    state: "NSW",
    teachingLevel: "high_school" as "primary" | "high_school" | "k12" | "tertiary" | "special_ed" | "other",
  });

  const handleNext = async () => {
    if (step === 1) {
      if (!profileData.name.trim()) {
        toast({ title: "Please enter your name", variant: "destructive" });
        return;
      }
      try {
        await updateProfile.mutateAsync({ data: { ...profileData, onboardingComplete: false } });
        setStep(2);
      } catch (err) {
        toast({
          title: "Couldn't save your details",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      }
    } else {
      try {
        // Include acknowledgement of current policy versions so the
        // /acknowledge gate immediately sees alreadyAcknowledged=true
        // and routes straight to /app/diary. The server echoes the
        // current PRIVACY_VERSION / TERMS_VERSION in the profile
        // response, so we pass them back here to satisfy the validation
        // that all three ack fields must arrive together.
        const ackFields =
          profile?.currentPrivacyVersion && profile?.currentTermsVersion
            ? {
                privacyAcknowledgedAt: new Date().toISOString(),
                privacyAcknowledgedVersion: profile.currentPrivacyVersion,
                termsAcknowledgedVersion: profile.currentTermsVersion,
              }
            : {};
        await updateProfile.mutateAsync({
          data: { ...profileData, onboardingComplete: true, ...ackFields },
        });
      } catch {
        // best-effort — still navigate
      }
      // Full reload clears stale React Query cache so AuthGuard sees the
      // updated onboardingComplete=true before deciding where to redirect.
      window.location.href = window.location.origin + (import.meta.env.BASE_URL || "/") + "acknowledge";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border">

        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b">
          <div className="flex items-center gap-3 mb-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center transition-colors ${
                  s < step ? "bg-green-500 text-white" : s === step ? "bg-primary text-white" : "bg-gray-100 text-gray-400"
                }`}>
                  {s < step ? <CheckCircle2 className="h-4 w-4" /> : s}
                </div>
                {s < 2 && <div className={`h-px w-8 transition-colors ${s < step ? "bg-green-400" : "bg-gray-200"}`} />}
              </div>
            ))}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {step === 1 && "Welcome to ClassTrack"}
            {step === 2 && "You're all set!"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === 1 && "Tell us a little about yourself"}
            {step === 2 && "Your account is ready to use"}
          </p>
        </div>

        <div className="px-8 py-6">

          {/* Step 1 — Profile */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label>Your name *</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="e.g. Sarah Johnson"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Teaching level</Label>
                <Select
                  value={profileData.teachingLevel}
                  onValueChange={(v) => setProfileData({ ...profileData, teachingLevel: v as "primary" | "high_school" | "k12" | "tertiary" | "special_ed" | "other" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary School</SelectItem>
                    <SelectItem value="high_school">High School / Secondary</SelectItem>
                    <SelectItem value="tertiary">Tertiary / TAFE</SelectItem>
                    <SelectItem value="special_ed">Special Education</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select
                    value={profileData.country}
                    onValueChange={(v) => setProfileData({ ...profileData, country: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="New Zealand">New Zealand</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>State / Region</Label>
                  <Input
                    value={profileData.state}
                    onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                    placeholder="e.g. NSW"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Done */}
          {step === 2 && (
            <div className="py-4 space-y-4">
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <p className="text-gray-700">
                  Welcome, <strong>{profileData.name}</strong>. Your account is ready.
                </p>
                <p className="text-sm text-gray-500">
                  Head to the diary to get started. You can set up your teaching timetable whenever you're ready.
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-8 pb-8 flex justify-between items-center">
          {step > 1 ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500"
              onClick={() => setStep((s) => s - 1)}
              disabled={updateProfile.isPending}
            >
              Back
            </Button>
          ) : (
            <div />
          )}
          <Button
            onClick={handleNext}
            disabled={updateProfile.isPending}
            className={step === 2 ? "ml-auto" : ""}
          >
            {updateProfile.isPending
              ? "Saving…"
              : step === 2
              ? "Go to my diary →"
              : "Continue"}
          </Button>
        </div>

      </div>
    </div>
  );
}
